import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { useScrollDirection } from 'react-use-scroll-direction';
import { useAppDispatch } from 'hooks';
import { setIsScrollAtTheBottom } from 'reducers/workflows';
import { useInView } from 'react-intersection-observer';
import { Intro } from './Intro';
import { Box } from '@mui/material';

interface ScrollableMessagesContainerProps {
  children?: React.ReactNode;
}

const ScrollableMessagesContainer = ({
  children,
}: ScrollableMessagesContainerProps) => {
  const dispatch = useAppDispatch();

  const ref = useRef<Element | null>();

  const { inView, ref: inViewRef } = useInView({
    fallbackInView: true,
    initialInView: true,
    onChange: inViewFromOnChange => {
      if (inViewFromOnChange) {
        dispatch(setIsScrollAtTheBottom(true));
      }
    },
  });

  const setRefs = useCallback(
    (node: Element | null | undefined) => {
      // Ref's from useRef needs to have the node assigned to `current`
      ref.current = node;
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node);
    },
    [inViewRef],
  );

  const { isScrollingUp, scrollTargetRef } = useScrollDirection();

  useEffect(() => {
    // `!inView` check is needed because sometimes `isScrollingUp` ends up being
    // `true` even though there was no scrolling:
    if (isScrollingUp && !inView) {
      dispatch(setIsScrollAtTheBottom(false));
    }
  }, [dispatch, inView, isScrollingUp]);

  return (
    <AgathaMessageGroupContainer
      className='js-scrollable-messages-container forethought-scrollbar'
      // @ts-expect-error react-use-scroll-direction is old, most likely types are a bit incorrect
      ref={scrollTargetRef}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          // -1px for the bottom scroll position detector
          minHeight: 'calc(100% - 1px)',
        }}
      >
        {/* Intro component */}
        <Intro />
        <ChildrenContainer>
          {/* Widget components */}
          {children}
        </ChildrenContainer>
      </Box>
      {/* Bottom scroll position detector */}
      <div
        ref={setRefs}
        style={{
          // Doesn't work without height:
          height: '1px',
        }}
      ></div>
    </AgathaMessageGroupContainer>
  );
};

const AgathaMessageGroupContainer = styled('div')`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden; // This is to hide the scrollbar when a carousel is visible

  &:not(.has-link) + & {
    margin-top: -12px;
  }
`;

const ChildrenContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-end;
  max-width: 100%;
  padding-top: 16px;
`;

export default ScrollableMessagesContainer;
